import { RECORD_TYPES } from '../utils/recordUtils';
import { getPageScale } from '../utils/pageScale';

const GEMINI_API_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-lite:generateContent';
const GEMINI_API_KEY_STORAGE = 'gemini_api_key';

const DEFAULT_TIMEOUT_MS = 45000;

// 답이 오지 않은 이유를 사람 말로 옮긴다. 이게 없으면 전부 '응답이 비어있습니다'로 끝난다
const FINISH_REASON_MESSAGE = {
  MAX_TOKENS: '응답이 너무 길어져 잘렸어요. 기록이 많으면 조금 줄여보세요.',
  SAFETY: '안전 필터에 걸려 응답이 생성되지 않았어요.',
  RECITATION: '인용이 길어 응답이 차단됐어요. 인용을 줄이면 됩니다.',
  PROHIBITED_CONTENT: '허용되지 않는 내용으로 판단돼 응답이 생성되지 않았어요.',
  BLOCKLIST: '차단된 표현이 있어 응답이 생성되지 않았어요.'
};

export const getGeminiApiKey = () => {
  try {
    const localKey = localStorage.getItem(GEMINI_API_KEY_STORAGE);
    if (localKey) return localKey;
    return import.meta.env.VITE_GEMINI_API_KEY || '';
  } catch {
    return '';
  }
};

export const setGeminiApiKey = (key) => {
  try {
    if (key) {
      localStorage.setItem(GEMINI_API_KEY_STORAGE, key);
    } else {
      localStorage.removeItem(GEMINI_API_KEY_STORAGE);
    }
    return true;
  } catch {
    return false;
  }
};

/**
 * Gemini 호출 공통 경로.
 *
 * 예전에는 호출부마다 `parts[0].text` 하나만 꺼냈다. 모델이 답을 여러 조각으로
 * 나눠 보내거나 생각(thought) 조각을 앞에 붙이면 그 자리가 비어서, 답이 왔는데도
 * '응답이 비어있습니다'로 끝났다. 조각을 모두 이어 붙이고, 그래도 비면
 * finishReason 을 사람이 읽을 수 있는 말로 바꿔 던진다.
 */
const callGemini = async (prompt, {
  temperature = 0.7,
  maxOutputTokens = 2048,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  label = 'AI 요청',
  image = null,  // { mimeType, data(base64) } — 이미지를 함께 보여줄 때
  json = false   // 답을 JSON 으로만 받을 때
} = {}) => {
  const apiKey = getGeminiApiKey();
  if (!apiKey) throw new Error('설정에서 Gemini API 키를 먼저 등록해주세요.');

  // 응답이 오지 않으면 버튼이 '생성 중...'에 갇힌다. 끊어줄 사람이 필요하다
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let response;
  try {
    response = await fetch(`${GEMINI_API_URL}?key=${apiKey}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{
          parts: [
            { text: prompt },
            ...(image ? [{ inline_data: { mime_type: image.mimeType, data: image.data } }] : [])
          ]
        }],
        generationConfig: {
          temperature,
          maxOutputTokens,
          ...(json ? { responseMimeType: 'application/json' } : {})
        }
      }),
      signal: controller.signal
    });
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new Error(`${label}이 오래 걸려 중단했어요. 다시 시도해주세요.`);
    }
    throw new Error(`${label} 중 연결에 실패했어요. 네트워크를 확인해주세요.`);
  } finally {
    clearTimeout(timer);
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const detail = errorData?.error?.message || '';

    if (response.status === 429) {
      throw new Error('Gemini 사용량 한도에 걸렸어요. 잠시 후 다시 시도해주세요.');
    }
    if (response.status === 400 && /api.?key/i.test(detail)) {
      throw new Error('Gemini API 키가 올바르지 않아요. 설정에서 다시 확인해주세요.');
    }
    if (response.status === 404) {
      throw new Error('Gemini 모델을 찾을 수 없어요. API 키의 사용 권한을 확인해주세요.');
    }
    throw new Error(detail || `${label} 실패 (HTTP ${response.status})`);
  }

  const data = await response.json();

  const blockReason = data?.promptFeedback?.blockReason;
  if (blockReason) throw new Error(`요청이 차단됐어요 (${blockReason}).`);

  const candidate = data?.candidates?.[0];
  const text = (candidate?.content?.parts || [])
    .filter(part => typeof part?.text === 'string' && !part.thought)
    .map(part => part.text)
    .join('')
    .trim();

  if (text) return text;

  const reason = candidate?.finishReason;
  throw new Error(
    FINISH_REASON_MESSAGE[reason]
    || `${label}에서 빈 응답을 받았어요${reason ? ` (${reason})` : ''}.`
  );
};

const SYSTEM_PROMPT = `당신은 사용자의 독서를 곁에서 지켜봐 온 독서 코치입니다.

**가장 중요한 규칙 — 한 권만 다룹니다**:
아래 '사실 데이터'의 **집중 도서 한 권**에 대해서만 말하세요.
- 그 책 외의 다른 책 제목을 꺼내지 마세요
- 다른 책과 견주거나, 두 책의 주제를 잇거나, "함께 읽고 계신 책들"처럼
  묶어서 말하지 마세요. 사용자는 이런 억지 연결을 원하지 않습니다
- 재료로 주어진 기록은 전부 그 한 권의 것입니다. 그 안에서만 이야기하세요

**당신이 보는 것**: 그 책을 얼마나 읽었는지(쪽수·진도)와, 무엇을 생각했는지
(그 책에 남긴 인용과 생각). 숫자만 읊지 마세요. 사용자가 남긴 문장이야말로
가장 좋은 재료입니다.

**좋은 메시지란**:
- 사용자가 스스로 알아채지 못했을 지점을 짚는다
- 남긴 인용·생각이 있다면 그 내용에 반응한다. 무엇에 꽂혀 있는지, 어떤 물음이
  반복되는지, 그 책의 지난 기록과 어떻게 이어지는지
- 페이스나 진도는 의미가 있을 때만 꺼낸다

**길이**: 2~5문장. 할 말이 있으면 길게, 없으면 짧게. 억지로 늘리지 마세요.

**정확성**:
- 아래 '사실 데이터'에 있는 수치만 인용하세요. 없는 수치를 지어내면 안 됩니다
- 단순한 비교나 판단(늘었다·줄었다, 며칠 남았다, 평균보다 많다)은 괜찮습니다
- 인용문을 언급할 때는 사용자가 쓴 표현을 그대로 쓰세요
- 기간은 "이번 주"가 아니라 "최근 7일"로 표현

**금지**:
- 집중 도서가 아닌 다른 책 언급 (권유·비교·연결 전부)
- 내용 없는 칭찬 ("대단해요", "훌륭해요", "멋져요", "화이팅")
- 죄책감 유발 ("오늘은 안 읽으셨네요")
- 형식적 인사 ("안녕하세요")
- 이모지 (최대 1개, 없어도 됨)
- 사용자가 쓰지 않은 생각을 쓴 것처럼 말하기

한국어 존댓말. 메시지 본문만 출력하세요.`;

export const generateCoachingMessage = async (stateSummary, previousMessage = null) => {
  if (!getGeminiApiKey()) return null;

  const contextString = typeof stateSummary === 'string'
    ? stateSummary
    : JSON.stringify(stateSummary, null, 2);

  let prompt = `${SYSTEM_PROMPT}

${contextString}`;

  if (previousMessage) {
    prompt += `

**직전 메시지** (동일 유형/표현 반복 금지):
"${previousMessage}"`;
  }

  prompt += `

위 자료에서 사용자가 스스로 인지하지 못했을 지점을 찾아 한 마디 건네세요. 메시지만 출력하세요.`;

  // 코칭은 배경에서 돌기도 해서, 실패하면 조용히 넘긴다 (호출측이 안내를 맡는다)
  try {
    return await callGemini(prompt, {
      temperature: 0.8,
      maxOutputTokens: 1200,
      label: '코치 메시지'
    });
  } catch (error) {
    console.error('Failed to generate coaching message:', error.message);
    return null;
  }
};

const REVIEW_PROMPT = `당신은 독자가 남긴 기록을 재료로 독후감 초안을 쓰는 사람입니다.

**이 글이 무엇이 아닌지부터**:
- 줄거리 요약이 아닙니다. 사건을 일어난 순서대로 늘어놓으면 실패입니다
- 기록을 이어 붙인 것이 아닙니다. 남긴 순서대로 훑고 지나가면 실패입니다
- 책 소개나 등장인물 정리가 아닙니다
- 재료를 빠짐없이 다루는 글이 아닙니다. 대부분은 버려야 합니다

**해야 할 일** (순서대로):
1. 기록 전체를 읽고, 독자가 자꾸 돌아간 지점 하나를 찾는다.
   되풀이되는 물음, 계속 눈에 밟힌 인물, 한 번 걸린 뒤 여러 번 따라붙는 장면 같은 것
2. 그 하나만으로 글을 쓴다. 초점에서 벗어난 기록은 아무리 흥미로워도 버린다
3. 근거로 쓸 기록 서너 개를 고르고 그것들을 끝까지 밀고 간다.
   **나머지는 억지로 끼워 넣지 않는다. 기록을 한 문장씩 붙여 나열하면 실패다**

**쓰는 법**:
- 사건 설명은 그 생각을 세우는 데 필요한 만큼만. 한두 문장을 넘기지 않는다
- 독자가 적어둔 생각이 이 글의 뼈대다. 옮겨 적은 문장은 그 생각을 받치는 근거로 쓴다
- 문단 하나에 생각 하나. 그 생각을 끝까지 전개한다 (최소 세 문장)
- 3~4문단, 전체 900자 안팎
- 첫 문장은 독자의 기록 중 가장 밀도 높은 지점에서 시작한다.
  "이 책은 ~한 책이다" 같은 소개형 첫 문장 금지
- 한국어 평어체(-다). 존댓말·에세이 투 금지
- 마무리에 억지 교훈을 붙이지 않는다. 물음으로 끝나도 좋다

**지어내지 않기**:
- 기록에 없는 줄거리·배경지식·평론을 끌어오지 않는다
- 다만 기록에 있는 것을 해석하고 잇는 일은 당신의 몫이다.
  독자가 두 곳에 남긴 것 사이의 관계를 짚는 것은 지어내는 것이 아니다
- 인용은 원문 그대로, 인용부호 안에

**출력 형식** (반드시 지킬 것):
<focus>이 글이 다룰 초점 한 문장</focus>
<draft>
본문만. 초안이라는 말이나 사족은 넣지 않는다
</draft>`;

const MAX_THOUGHTS = 40;
const MAX_QUOTES = 30;
const MAX_RECORD_CHARS = 500;

const clip = (text = '') =>
  text.length > MAX_RECORD_CHARS ? `${text.slice(0, MAX_RECORD_CHARS)}…` : text;

/**
 * 개수를 줄일 때 앞에서부터 자르지 않고 고르게 솎아낸다.
 * 앞부분만 남기면 책 뒤쪽 기록을 통째로 못 보고, 초반 줄거리만 훑는 글이 나온다.
 */
const sample = (list, limit) => {
  if (limit <= 0) return [];
  if (list.length <= limit) return list;
  if (limit === 1) return [list[0]];

  // 양 끝을 반드시 포함한다 — 책의 끝을 못 보면 마무리를 다룰 수가 없다
  const last = list.length - 1;
  const picked = Array.from(
    { length: limit },
    (_, i) => list[Math.round((i * last) / (limit - 1))]
  );
  return [...new Set(picked)];
};

/**
 * 기록을 AI 가 읽을 재료로 정리한다.
 *
 * 종류별로 나눠서 넘기는 게 핵심이다. 페이지 순으로 섞어 넘기면 모델이 그 순서를
 * 글의 순서로 삼아 줄거리를 훑는다. 독자가 적어둔 '생각'이 글의 뼈대이고
 * 옮겨 적은 '인용'은 근거이므로, 역할이 드러나게 갈라놓는다.
 *
 * 인용에 달아둔 생각은 그 인용 아래에 붙인다 — 따로 떼면 무엇에 대한 생각인지
 * 모르는 채로 쓰게 된다.
 */
export const buildReviewMaterial = (book, records = []) => {
  const scale = getPageScale(book);
  const byPage = (a, b) => (a.page || 0) - (b.page || 0);
  const page = (record) => (record.page ? `p.${scale.toDisplay(record.page)} ` : '');

  const roots = records.filter(r => !r.parentId).sort(byPage);
  const childrenOf = (id) => records.filter(r => r.parentId === id).sort(byPage);

  const thoughts = roots.filter(r => r.type !== RECORD_TYPES.QUOTE);
  const quotes = roots.filter(r => r.type === RECORD_TYPES.QUOTE);

  // 생각이 달린 인용은 독자가 실제로 붙들었던 문장이다. 솎아낼 때 먼저 살린다
  const engaged = quotes.filter(q => childrenOf(q.id).length > 0);
  const plain = quotes.filter(q => childrenOf(q.id).length === 0);

  const pickedThoughts = sample(thoughts, MAX_THOUGHTS);
  const pickedQuotes = [
    ...sample(engaged, MAX_QUOTES),
    ...sample(plain, Math.max(0, MAX_QUOTES - engaged.length))
  ].sort(byPage);

  const sections = [];

  if (pickedThoughts.length > 0) {
    sections.push(
      `**독자가 적어둔 생각** (${pickedThoughts.length}개) — 이 글의 뼈대입니다:\n`
      + pickedThoughts.map(r => `- ${page(r)}${clip(r.text)}`).join('\n')
    );
  }

  if (pickedQuotes.length > 0) {
    sections.push(
      `**독자가 옮겨 적은 문장** (${pickedQuotes.length}개) — 근거로 쓸 재료입니다:\n`
      + pickedQuotes.map(q => {
        const lines = [`- ${page(q)}"${clip(q.text)}"`];
        childrenOf(q.id).forEach(child => {
          lines.push(`  └ 이 문장에 독자가 붙인 생각: ${clip(child.text)}`);
        });
        return lines.join('\n');
      }).join('\n')
    );
  }

  return {
    material: sections.join('\n\n'),
    thoughtCount: pickedThoughts.length,
    quoteCount: pickedQuotes.length,
    omitted: roots.length - pickedThoughts.length - pickedQuotes.length
  };
};

/** <draft> 안쪽만 꺼낸다. 태그를 안 지키면 통째로 쓰되 흔적은 지운다 */
export const extractDraft = (text = '') => {
  const match = text.match(/<draft>([\s\S]*?)<\/draft>/i);
  if (match) return match[1].trim();
  return text
    .replace(/<\/?(?:draft|focus)>/gi, '')
    .replace(/^\s*초점\s*[:：].*$/m, '')
    .trim();
};

/**
 * 책의 기록으로 독후감 초안을 만든다.
 * @param {Object} book
 * @param {Array} records 이 책의 기록 (인용/생각)
 * @param {Object} options
 * @param {string} options.focus 독자가 직접 고른 초점 (선택)
 */
export const generateReview = async (book, records = [], { focus } = {}) => {
  if (records.length < 3) throw new Error('기록이 3개 이상 필요합니다.');

  const { material, thoughtCount, quoteCount, omitted } = buildReviewMaterial(book, records);
  if (!material) throw new Error('재료로 쓸 기록을 찾지 못했어요.');

  const wanted = focus?.trim();
  const parts = [
    REVIEW_PROMPT,
    '',
    `**책**: ${book.title}${book.author ? ` (${book.author})` : ''}`
  ];

  if (book.oneLiner) parts.push(`**독자의 한줄평**: ${book.oneLiner}`);

  // 독자가 초점을 직접 골랐으면 찾는 단계를 건너뛴다
  if (wanted) {
    parts.push(
      '',
      `**독자가 정한 초점**: ${wanted}`,
      '초점을 새로 찾지 말고 이 초점으로 쓰세요. 여기에 닿지 않는 기록은 버리세요.'
    );
  }

  if (omitted > 0) {
    parts.push('', `(기록 ${omitted}개는 분량 때문에 뺐습니다. 남은 것만으로 쓰세요)`);
  }

  parts.push('', material);

  // 생각 없이 인용만 있으면 뼈대가 될 재료가 없다. 그 사정을 알려준다
  if (thoughtCount === 0 && quoteCount > 0) {
    parts.push(
      '',
      '독자가 따로 적어둔 생각은 없고 옮겨 적은 문장만 있습니다.'
      + ' 어떤 문장들을 골랐는지에서 관심사를 읽어내 그것을 초점으로 삼으세요.'
    );
  }

  // 독후감은 길다. 토큰이 모자라면 본문이 통째로 잘려 빈 응답처럼 보인다
  const raw = await callGemini(parts.join('\n'), {
    temperature: 0.8,
    maxOutputTokens: 4096,
    timeoutMs: 90000,
    label: '독후감 생성'
  });

  const draft = extractDraft(raw);
  if (!draft) throw new Error('초안 본문을 받지 못했어요. 다시 시도해주세요.');
  return draft;
};

const BOOK_SEARCH_PROMPT = `당신은 책을 찾아주는 사서입니다.

사용자가 기억나는 대로 설명한 책을 후보로 추려주세요.

**출력**: 반드시 아래 JSON 배열만 출력하세요. 다른 텍스트 없이 JSON만.

[
  { "title": "정확한 책 제목", "author": "저자명", "reason": "왜 이 책인지 한 문장" }
]

**규칙**:
- 최대 5권
- 한국어로 출간된 책이면 한국어 제목으로, 아니면 원제로
- 제목은 검색에 그대로 쓸 수 있도록 부제 없이 정확하게
- 확신이 낮아도 후보를 제시하되, reason 에 근거를 밝히세요
- 도저히 짐작할 수 없으면 빈 배열 []`;

/**
 * 자연어 설명으로 책 후보를 찾는다.
 * 결과는 제목 문자열이므로, 호출측이 카카오/Open Library 에 다시 조회해
 * 표지·페이지 수 같은 실제 메타데이터를 채운다.
 */
export const searchBooksByDescription = async (description) => {
  const text = await callGemini(
    `${BOOK_SEARCH_PROMPT}\n\n**사용자 설명**:\n${description}`,
    { temperature: 0.3, maxOutputTokens: 2048, label: 'AI 검색' }
  );

  const parsed = extractJsonArray(text);
  if (!parsed) throw new Error('AI 응답을 이해하지 못했습니다');

  return parsed
    .filter(b => b.title && typeof b.title === 'string')
    .map(b => ({
      title: b.title.trim(),
      author: typeof b.author === 'string' ? b.author.trim() : '',
      reason: typeof b.reason === 'string' ? b.reason.trim() : ''
    }));
};

const TOC_PARSE_PROMPT = `당신은 책의 목차 텍스트를 구조화된 JSON으로 변환하는 파서입니다.

**입력**: 사용자가 붙여넣은 목차 텍스트 (형식이 다양할 수 있음)
**출력**: 반드시 아래 JSON 형식만 출력하세요. 다른 텍스트 없이 JSON만.

[
  {
    "title": "메인 챕터 제목",
    "subChapters": ["소챕터1", "소챕터2"]
  }
]

**파싱 규칙**:
- 숫자+장, 제N장, Chapter, Part, 프롤로그, 에필로그 등 → 메인 챕터
- 들여쓰기, -, ·, •, │ 구분자, 또는 메인 챕터 아래 나열된 항목 → 소챕터
- 소챕터가 없는 챕터는 "subChapters": []
- 원본 텍스트의 순서를 유지
- 챕터 제목에서 불필요한 번호 접두사(1., 01 등)는 유지 (원문 그대로)`;

// JSON 텍스트에서 배열 추출 (여러 전략 시도)
const extractJsonArray = (raw) => {
  // 1단계: 코드펜스 제거
  let text = raw.replace(/```(?:json)?\s*/gi, '').replace(/```/gi, '').trim();

  // 2단계: 직접 파싱 시도
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) return parsed;
  } catch { /* 다음 전략 시도 */ }

  // 3단계: 텍스트에서 JSON 배열 부분만 추출 ([ ... ] 사이)
  const firstBracket = text.indexOf('[');
  const lastBracket = text.lastIndexOf(']');
  if (firstBracket >= 0 && lastBracket > firstBracket) {
    const slice = text.substring(firstBracket, lastBracket + 1);
    // 트레일링 콤마 수정
    const cleaned = slice.replace(/,\s*([}\]])/g, '$1');
    try {
      const parsed = JSON.parse(cleaned);
      if (Array.isArray(parsed)) return parsed;
    } catch { /* 다음 전략 시도 */ }
  }

  // 4단계: 잘린 JSON 복구 (마지막 완전한 객체까지)
  const arrayStart = text.indexOf('[');
  if (arrayStart >= 0) {
    const arrayText = text.substring(arrayStart);
    const lastObj = arrayText.lastIndexOf('}');
    if (lastObj > 0) {
      const truncated = arrayText.substring(0, lastObj + 1) + ']';
      const cleaned = truncated.replace(/,\s*([}\]])/g, '$1');
      try {
        const parsed = JSON.parse(cleaned);
        if (Array.isArray(parsed)) return parsed;
      } catch { /* 최종 실패 */ }
    }
  }

  return null;
};

export const parseTableOfContents = async (tocText) => {
  const text = await callGemini(
    `${TOC_PARSE_PROMPT}\n\n**목차 텍스트**:\n${tocText}`,
    { temperature: 0, maxOutputTokens: 8192, timeoutMs: 60000, label: '목차 파싱' }
  );

  const parsed = extractJsonArray(text);
  if (!parsed || parsed.length === 0) {
    throw new Error('JSON 파싱 실패. AI 원본 응답: ' + text.substring(0, 200));
  }

  // 유효성 검증
  return parsed
    .filter(ch => ch.title && typeof ch.title === 'string')
    .map(ch => ({
      title: ch.title.trim(),
      subChapters: Array.isArray(ch.subChapters)
        ? ch.subChapters.filter(s => typeof s === 'string').map(s => s.trim())
        : []
    }));
};

const QUOTE_IMAGE_PROMPT = `이 이미지는 전자책 앱에서 책 속 문장을 골라 카드로 만든 것입니다.
카드에 적힌 책 속 문장을 그대로 옮겨 적으세요.

- 글자를 고치거나 요약하거나 덧붙이지 마세요. 맞춤법이 틀려 보여도 보이는 대로 적습니다
- 앱 이름, 로고, 워터마크, 날짜, 버튼 글자는 문장에 넣지 마세요
- 카드 폭 때문에 줄이 바뀐 곳은 이어 붙이고, 문단이 바뀐 곳만 줄을 바꾸세요
- 책 제목, 저자, 쪽수가 보이면 따로 적고, 보이지 않으면 빈 문자열로 두세요

다음 모양의 JSON 하나로만 답하세요:
{"quote": "", "title": "", "author": "", "page": ""}`;

/**
 * 이미지 판독 답을 꺼낸다. JSON 으로 오지 않으면 받은 글 전체를 문장으로 본다.
 * @returns {{ quote: string, title: string, author: string, page: string }}
 */
export const parseQuoteJson = (raw = '') => {
  const cleaned = String(raw).trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '');
  const asText = (value) => {
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    return '';
  };
  try {
    const data = JSON.parse(cleaned);
    if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('not an object');
    return {
      quote: asText(data.quote),
      title: asText(data.title),
      author: asText(data.author),
      page: asText(data.page).replace(/\D/g, '')
    };
  } catch {
    return { quote: cleaned, title: '', author: '', page: '' };
  }
};

/**
 * 이미지 속 책 문장 읽기. 교보eBook 처럼 문장을 카드 이미지로 공유하는 앱에서 받았을 때 쓴다.
 * @param {{ mimeType: string, data: string }} image base64 로 된 이미지
 */
export const readQuoteFromImage = async (image) => {
  const text = await callGemini(QUOTE_IMAGE_PROMPT, {
    image,
    json: true,
    temperature: 0,
    maxOutputTokens: 2048,
    timeoutMs: 30000,
    label: '이미지 판독'
  });
  return parseQuoteJson(text);
};
